import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDue, ukToday } from "@/lib/subjects";

export const RevisionPlan = ({ test, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const plan = Array.isArray(test.plan) ? test.plan : [];
  const done = plan.filter((s) => s.done).length;
  const running = busy || test.plan_status === "running";
  const today = ukToday();

  const make = async () => {
    setBusy(true);
    try { await api.post(`/homework/${test.hw_id}/plan`); toast.success("Revision plan ready"); setOpen(true); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const tick = async (i, v) => {
    try { await api.put(`/homework/${test.hw_id}/plan/${i}`, { done: v }); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!plan.length) {
    return (
      <button data-testid={`plan-make-${test.hw_id}`} onClick={make} disabled={running}
        className="mt-3 inline-flex items-center gap-2 rounded-xl border border-gold-400/50 bg-gold-50 px-3 py-2 text-xs font-semibold text-gold-300 hover:bg-gold-100 disabled:opacity-60 transition-colors">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{running ? "Writing your revision plan…" : "Make a revision plan"}
      </button>
    );
  }
  return (
    <div data-testid={`plan-${test.hw_id}`} className="mt-3">
      <div className="flex items-center gap-2">
        <button data-testid={`plan-toggle-${test.hw_id}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 text-xs font-semibold text-gold-300">
          Revision plan · {done}/{plan.length} done<ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="h-1.5 flex-1 max-w-[120px] rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-gold-400 transition-[width]" style={{ width: `${(done / plan.length) * 100}%` }} /></div>
      </div>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {plan.map((s, i) => (
            <li key={s.date} data-testid={`plan-session-${test.hw_id}-${i}`} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 text-sm ${s.date === today ? "border-gold-400/60 bg-gold-50" : "border-slate-200 bg-slate-50"}`}>
              <button data-testid={`plan-tick-${test.hw_id}-${i}`} onClick={() => tick(i, !s.done)} aria-label={s.done ? "Mark not done" : "Mark done"}
                className={`mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-md border-2 ${s.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-400"}`}>{s.done && <Check className="w-3.5 h-3.5" />}</button>
              <div className="min-w-0">
                <p className={s.done ? "line-through text-slate-500" : "text-slate-800"}>{s.task}</p>
                <p className="font-mono text-[11px] text-slate-500">{s.date === today ? "Today" : fmtDue(s.date)} · {s.minutes} min</p>
              </div>
            </li>
          ))}
          <li><button data-testid={`plan-redo-${test.hw_id}`} onClick={make} disabled={running} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Redo plan</button></li>
        </ul>
      )}
    </div>
  );
};
